if (!customElements.get('contact-file-upload')) {
  customElements.define(
    'contact-file-upload',
    class ContactFileUpload extends HTMLElement {
      constructor() {
        super();
        this.dropzone = this.querySelector('[data-dropzone]');
        this.fileInput = this.querySelector('[data-file-input]');
        this.list = this.querySelector('[data-file-list]');
        this.urlsField = this.querySelector('[data-urls-field]');
        this.status = this.querySelector('[data-upload-status]');
        this.form = this.closest('form');
        this.submitButton = this.form ? this.form.querySelector('button[type="submit"]') : null;

        this.cloudName = this.dataset.cloudName;
        this.uploadPreset = this.dataset.uploadPreset;
        this.maxFiles = parseInt(this.dataset.maxFiles, 10) || 5;
        this.maxSizeMb = parseInt(this.dataset.maxSize, 10) || 10;

        this.items = [];
        this.counter = 0;

        this.dropzone.addEventListener('click', () => this.fileInput.click());
        this.dropzone.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.fileInput.click();
          }
        });
        this.fileInput.addEventListener('change', () => {
          this.addFiles(this.fileInput.files);
          this.fileInput.value = '';
        });

        ['dragenter', 'dragover'].forEach((type) =>
          this.dropzone.addEventListener(type, (event) => {
            event.preventDefault();
            this.dropzone.classList.add('is-dragover');
          })
        );
        ['dragleave', 'drop'].forEach((type) =>
          this.dropzone.addEventListener(type, (event) => {
            event.preventDefault();
            this.dropzone.classList.remove('is-dragover');
          })
        );
        this.dropzone.addEventListener('drop', (event) => {
          if (event.dataTransfer && event.dataTransfer.files) this.addFiles(event.dataTransfer.files);
        });

        if (this.form) {
          this.form.addEventListener('submit', (event) => {
            if (this.items.some((item) => item.state === 'uploading')) {
              event.preventDefault();
              this.announce(this.dataset.textWait);
            }
          });
        }
      }

      addFiles(fileList) {
        Array.from(fileList).forEach((file) => {
          if (this.items.filter((item) => item.state !== 'error').length >= this.maxFiles) {
            this.announce(this.dataset.textMaxFiles.replace('[count]', this.maxFiles));
            return;
          }
          if (file.size > this.maxSizeMb * 1024 * 1024) {
            this.renderItem(file, 'error', this.dataset.textTooLarge.replace('[size]', this.maxSizeMb + ' Mo'));
            return;
          }
          this.upload(file);
        });
      }

      renderItem(file, state, message) {
        const id = 'upload-' + ++this.counter;
        const item = { id, file, state, url: null };
        this.items.push(item);

        const li = document.createElement('li');
        li.className = 'file-upload__item';
        li.id = id;

        const thumb = document.createElement('div');
        thumb.className = 'file-upload__thumb';
        if (file.type.startsWith('image/')) {
          const img = document.createElement('img');
          img.alt = '';
          img.src = URL.createObjectURL(file);
          img.addEventListener('load', () => URL.revokeObjectURL(img.src));
          thumb.appendChild(img);
        } else {
          thumb.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6z"/><path d="M14 3v6h6"/></svg>';
        }

        const info = document.createElement('div');
        info.className = 'file-upload__info';
        const name = document.createElement('span');
        name.className = 'file-upload__name';
        name.textContent = file.name;
        const meta = document.createElement('span');
        meta.className = 'file-upload__meta';
        meta.textContent = message || this.formatSize(file.size);
        const progress = document.createElement('div');
        progress.className = 'file-upload__progress';
        progress.innerHTML = '<div class="file-upload__progress-bar"></div>';
        info.append(name, meta, progress);

        const badge = document.createElement('span');
        badge.className = 'file-upload__badge';

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'file-upload__remove';
        remove.setAttribute('aria-label', this.dataset.textRemove + ' ' + file.name);
        remove.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';
        remove.addEventListener('click', () => this.removeItem(id));

        li.append(thumb, info, badge, remove);
        this.list.appendChild(li);

        this.setItemState(item, li, state, message);
        return { item, li };
      }

      setItemState(item, li, state, message) {
        item.state = state;
        li.dataset.state = state;
        const meta = li.querySelector('.file-upload__meta');
        const badge = li.querySelector('.file-upload__badge');
        if (state === 'error') {
          meta.textContent = message || this.dataset.textError;
          badge.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>';
        } else if (state === 'done') {
          meta.textContent = this.formatSize(item.file.size);
          badge.innerHTML =
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
        }
      }

      upload(file) {
        const { item, li } = this.renderItem(file, 'uploading');
        const bar = li.querySelector('.file-upload__progress-bar');

        const data = new FormData();
        data.append('file', file);
        data.append('upload_preset', this.uploadPreset);

        const xhr = new XMLHttpRequest();
        item.xhr = xhr;
        xhr.open('POST', 'https://api.cloudinary.com/v1_1/' + encodeURIComponent(this.cloudName) + '/auto/upload');
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) bar.style.width = (event.loaded / event.total) * 100 + '%';
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              item.url = JSON.parse(xhr.responseText).secure_url;
              this.setItemState(item, li, 'done');
              this.announce(this.dataset.textDone.replace('[name]', file.name));
            } catch (error) {
              this.setItemState(item, li, 'error');
            }
          } else {
            this.setItemState(item, li, 'error');
          }
          this.syncField();
        });
        xhr.addEventListener('error', () => {
          this.setItemState(item, li, 'error');
          this.syncField();
        });
        xhr.send(data);
        this.syncField();
      }

      removeItem(id) {
        const index = this.items.findIndex((item) => item.id === id);
        if (index === -1) return;
        const item = this.items[index];
        if (item.xhr && item.state === 'uploading') item.xhr.abort();
        this.items.splice(index, 1);
        const li = document.getElementById(id);
        if (li) li.remove();
        this.syncField();
      }

      syncField() {
        const urls = this.items.filter((item) => item.url).map((item) => item.url);
        this.urlsField.value = urls.join('\n');
        const uploading = this.items.some((item) => item.state === 'uploading');
        if (this.submitButton) {
          this.submitButton.disabled = uploading;
          this.submitButton.classList.toggle('file-upload__submit-waiting', uploading);
        }
      }

      announce(text) {
        this.status.textContent = text;
      }

      formatSize(bytes) {
        if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1).replace('.', ',') + ' Mo';
        return Math.max(1, Math.round(bytes / 1024)) + ' Ko';
      }
    }
  );
}
